import React, { FC, forwardRef } from 'react';
import { PickersCalendarHeaderProps, usePickerTranslations, usePickerAdapter } from '@mui/x-date-pickers';
import { Box, Button, IconButton, IconButtonProps, ButtonProps } from '@mui/material';
import { ChevronLeftIcon } from '@/src/components/icon/stroke/ChevronLeftIcon';
import { ChevronRightIcon } from '@/src/components/icon/stroke/ChevronRightIcon';
import { ChevronDownIcon } from '@/src/components/icon/stroke/ChevronDownIcon';
import { getTypography } from '@/src/materialui/typography/typography.theme';

/**
 * Customization of header in Date/Time pickers
 */
export const FlowPickersCalendarHeader: FC<PickersCalendarHeaderProps> = forwardRef(
  ({ view, views, currentMonth, onMonthChange, onViewChange, className }, ref) => {
    const translations = usePickerTranslations();
    const adapter = usePickerAdapter();
    const selectNextMonth = () => onMonthChange(adapter.addMonths(currentMonth, 1));
    const selectPreviousMonth = () => onMonthChange(adapter.addMonths(currentMonth, -1));
    const selectNextYear = () => onMonthChange(adapter.addYears(currentMonth, 1));
    const selectPreviousYear = () => onMonthChange(adapter.addYears(currentMonth, -1));

    return (
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'surface.secondary',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          // height affects datecalendar.theme
          px: 2.5,
          py: 1.75,
          // prevent jumping when switching between calendars
          boxSizing: 'border-box',
          minHeight: '61px',
        }}
        ref={ref}
        className={className}
      >
        {views.includes('month') ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '50%' }}>
            <PickersCalendarHeaderIconButton
              onClick={selectPreviousMonth}
              aria-label={`${translations.openPreviousView} (${translations.month})`}
              disabled={view !== 'day'}
            >
              <ChevronLeftIcon />
            </PickersCalendarHeaderIconButton>
            <PickersCalendarHeaderButton
              active={view === 'month'}
              onClick={() => {
                onViewChange(view === 'month' ? 'day' : 'month');
              }}
              disabled={!views.includes('day') && view === 'month'}
              aria-label={translations.month}
            >
              {adapter.format(currentMonth, 'monthShort')}
            </PickersCalendarHeaderButton>
            <PickersCalendarHeaderIconButton
              onClick={selectNextMonth}
              aria-label={`${translations.openNextView} (${translations.month})`}
              disabled={view !== 'day'}
            >
              <ChevronRightIcon />
            </PickersCalendarHeaderIconButton>
          </Box>
        ) : (
          <Box />
        )}
        {views.includes('year') && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '50%' }}>
            <PickersCalendarHeaderIconButton
              onClick={selectPreviousYear}
              aria-label={`${translations.openPreviousView} (${translations.year})`}
              disabled={view !== 'day'}
            >
              <ChevronLeftIcon />
            </PickersCalendarHeaderIconButton>
            <PickersCalendarHeaderButton
              active={view === 'year'}
              onClick={() => {
                onViewChange(view === 'year' ? 'day' : 'year');
              }}
              disabled={!views.includes('day') && view === 'year'}
              aria-label={translations.year}
            >
              {adapter.format(currentMonth, 'year')}
            </PickersCalendarHeaderButton>
            <PickersCalendarHeaderIconButton
              onClick={selectNextYear}
              aria-label={`${translations.openNextView} (${translations.year})`}
              disabled={view !== 'day'}
            >
              <ChevronRightIcon />
            </PickersCalendarHeaderIconButton>
          </Box>
        )}
      </Box>
    );
  },
);

const PickersCalendarHeaderIconButton: FC<IconButtonProps> = (props) => {
  return (
    <IconButton
      size="small"
      sx={{
        '&.Mui-disabled': {
          opacity: '0',
        },
        '& .MuiSvgIcon-root': {
          fontSize: '20px',
        },
      }}
      {...props}
    />
  );
};

const PickersCalendarHeaderButton: FC<ButtonProps & { active?: boolean }> = ({ active, ...rest }) => {
  return (
    <Button
      sx={(theme) => ({
        flexGrow: '1',
        py: 0.75,
        gap: 0.75,
        color: theme.palette.mode === 'dark' ? 'onSurface.os200' : 'onSurface.os700',
        [theme.breakpoints.up('xs')]: {
          ...getTypography('disclaimer1', theme.direction === 'rtl', 'large'),
        },
        '& .MuiButton-icon .MuiSvgIcon-root': {
          color: 'text.primary',
          fontSize: '20px',
        },
      })}
      endIcon={
        <ChevronDownIcon
          sx={{
            transform: active ? 'rotate(-180deg)' : 'rotate(0deg)',
          }}
        />
      }
      size="small"
      variant="text"
      color="secondary"
      {...rest}
    />
  );
};
